import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import type { Contact, UpdateContactRequest } from "@/api/generated/client/models";

interface ContactEditDialogProps {
    contact: Contact | null;
    onClose: () => void;
    updateContact: (args: { id: number; data: UpdateContactRequest }) => Promise<Contact>;
    isUpdating: boolean;
}

export function ContactEditDialog({ contact, onClose, updateContact, isUpdating }: ContactEditDialogProps) {
    const [form, setForm] = useState<UpdateContactRequest>({});

    useEffect(() => {
        if (contact) {
            setForm({
                contactType: contact.contactType,
                firstName: contact.firstName,
                lastName: contact.lastName,
                email: contact.email,
                phone: contact.phone,
                jobTitle: contact.jobTitle,
                notes: contact.notes,
            });
        }
    }, [contact]);

    const handleSave = async () => {
        if (!contact) return;
        try {
            await updateContact({ id: contact.id, data: form });
            onClose();
        } catch {
            // Error toast handled by mutation's onError callback
        }
    };

    return (
        <Dialog open={!!contact} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-lg max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto sm:overflow-auto">
                <DialogHeader>
                    <DialogTitle>Edit Contact</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>First Name <span className="text-destructive">*</span></Label>
                            <Input value={form.firstName || ""} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Last Name <span className="text-destructive">*</span></Label>
                            <Input value={form.lastName || ""} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Contact Type</Label>
                            <Input value={form.contactType || ""} onChange={(e) => setForm({ ...form, contactType: e.target.value || undefined })} placeholder="e.g. Homeowner, Adjuster" />
                        </div>
                        <div className="space-y-2">
                            <Label>Job Title</Label>
                            <Input value={form.jobTitle || ""} onChange={(e) => setForm({ ...form, jobTitle: e.target.value || null })} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value || null })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value || null })} />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value || null })} rows={3} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} disabled={!form.firstName || !form.lastName || isUpdating}>
                        {isUpdating ? "Saving..." : "Save Changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}